import { styles as orbitStyles } from "@withorbit/ui";
import React from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { router, usePathname } from "expo-router";

const { colors, layout, type } = orbitStyles;

export type NavigationRoute = "home" | "collections" | "create" | "import" | "review";

interface NavigationBarProps {
  currentRoute?: NavigationRoute;
  showHomeLink?: boolean;
}

const navItems: { route: NavigationRoute; label: string; path: string }[] = [
  { route: "home", label: "Home", path: "/" },
  { route: "collections", label: "Collections", path: "/collections" },
  { route: "create", label: "Create", path: "/create" },
  { route: "import", label: "Import", path: "/import" },
  { route: "review", label: "Review", path: "/review" },
];

// Orbit design system colors
const orbitColors = {
  accentPrimary: "#D60909",
  fgSecondarySmall: "#826D4E",
  hoverLayer: "rgba(92, 56, 0, 0.05)",
};

const componentStyles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    gap: layout.gridUnit * 2,
    paddingVertical: layout.gridUnit,
    marginBottom: layout.gridUnit * 2,
  },
  navItem: {
    paddingHorizontal: layout.gridUnit * 1.5,
    paddingVertical: layout.gridUnit * 0.75,
    borderRadius: layout.borderRadius,
  },
  navItemActive: {
    backgroundColor: orbitColors.hoverLayer,
  },
  navItemText: {
    fontFamily: "Dr-ExtraBold, system-ui, sans-serif",
    fontSize: 14,
    letterSpacing: 0.02,
    color: orbitColors.fgSecondarySmall,
  },
  navItemTextActive: {
    color: orbitColors.accentPrimary,
  },
});

export function NavigationBar({ currentRoute, showHomeLink = true }: NavigationBarProps) {
  const pathname = usePathname();

  // Auto-detect current route from pathname if not explicitly provided
  const activeRoute = currentRoute ?? (() => {
    if (pathname === "/" || pathname === "/learnMore") return "home";
    if (pathname === "/collections" || pathname.startsWith("/collection")) return "collections";
    if (pathname === "/create") return "create";
    if (pathname === "/import") return "import";
    if (pathname === "/review") return "review";
    if (pathname === "/prompt") return "collections"; // Prompt editor is part of collections flow
    return "home";
  })();

  const filteredItems = showHomeLink
    ? navItems
    : navItems.filter(item => item.route !== "home");

  return (
    <View style={componentStyles.container}>
      {filteredItems.map((item) => {
        const isActive = activeRoute === item.route;
        return (
          <Pressable
            key={item.route}
            onPress={() => {
              if (!isActive) {
                router.push(item.path);
              }
            }}
            style={[
              componentStyles.navItem,
              isActive && componentStyles.navItemActive,
            ]}
          >
            <Text
              style={[
                componentStyles.navItemText,
                isActive && componentStyles.navItemTextActive,
              ]}
            >
              {item.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

export default NavigationBar;
